import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProfileService } from './profile.service';
import { TokenService } from './token.service';
import { User } from '../../pages/users/models/user.model';
import {
  AGENT_ROLE_NAMES,
  SECRETARY_ROLE_NAMES,
} from '../interfaces/role-names';

export type StatsDashboardRole = 'admin' | 'agent' | 'operator';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private permissions: string[] = [];
  private roleNames: string[] = [];
  private loaded$ = new BehaviorSubject<boolean>(false);
  private isAdminUser = false;

  permissionsLoaded$ = this.loaded$.asObservable();

  constructor(
    private profileService: ProfileService,
    private tokenService: TokenService,
  ) {}

  isLoaded(): boolean {
    return this.loaded$.value;
  }

  isAdmin(): boolean {
    return this.isAdminUser;
  }

  hasRole(roleName: string): boolean {
    const normalizedRoleName = this.normalizeRoleName(roleName);
    const hasExactRole = this.roleNames.some(
      (role) => role === normalizedRoleName,
    );

    if (hasExactRole) return true;

    const isAgentRole = AGENT_ROLE_NAMES.includes(normalizedRoleName);

    return (
      isAgentRole &&
      this.roleNames.some((role) => SECRETARY_ROLE_NAMES.includes(role))
    );
  }

  hasAnyRole(roleNames: readonly string[]): boolean {
    return (roleNames || []).some((roleName) => this.hasRole(roleName));
  }

  isOperator(): boolean {
    return (
      !this.isAdminUser &&
      ['operator', 'اپراتور'].some((roleName) => this.hasRole(roleName))
    );
  }

  getStatsDashboardRole(): StatsDashboardRole | null {
    if (this.isAdmin()) return 'admin';
    if (this.hasAnyRole(AGENT_ROLE_NAMES)) return 'agent';
    if (this.isOperator()) return 'operator';
    return null;
  }

  setPermissions(perms: string[]) {
    this.permissions = perms;
    this.loaded$.next(true);
  }

  hasPermission(section: string): boolean {
    // Admin role has access to every section and action
    // if (this.isAdminUser) {
    //   return true;
    // }

    const result = this.permissions.includes(section) ?? false;
    return result
  }

  loadPermissionsFromProfile(): Promise<void> {
    return this.profileService
      .getProfile()
      .toPromise()
      .then((res) => {
        this.applyPermissionsFromUser(res?.data);
      })
      .catch(() => {
        const cachedUser = this.getCachedUser();

        if (cachedUser) {
          this.applyPermissionsFromUser(cachedUser);
          return;
        }

        this.isAdminUser = false;
        this.roleNames = [];
        this.setPermissions([]);
      });
  }

  private applyPermissionsFromUser(user?: User | null) {
    const roles = user?.roles || [];
    const permissions = []
    this.roleNames = roles.map((role) => this.normalizeRoleName(role?.name));

    // Check if current user has an "Admin" role (case-insensitive)
    this.isAdminUser = roles.some(
      (role) => this.normalizeRoleName(role?.name) === 'admin',
    );

    roles.forEach((role) => {
      (role.permissions || []).forEach((p) => {
        permissions.push(p)
      });
    });

    this.setPermissions(permissions);
  }

  private normalizeRoleName(roleName?: string | null): string {
    return String(roleName || '').trim().toLowerCase();
  }

  private getCachedUser(): User | null {
    try {
      return this.tokenService.getUser<User>();
    } catch {
      return null;
    }
  }
}
